import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Formik, FieldArray } from "formik";
import _ from "lodash";
import styled, { keyframes } from "styled-components";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core";
import ActionBar from "../../common/ActionBar";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import {
  CustomInputField,
  CustomFileUpload,
  CustomCheckbox,
} from "../../common/FormInputs";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { CustomTextArea } from "../../common/FormField/FormField";
import AWSService from "../../../services/AWSService";
import SystemService from "../../../services/SystemService";
import { navigate } from "@reach/router";
import { getFileName } from "../../../helpers/functions";

const EMPTY_WEBINAR = {
  slug: "",
  status: "",

  navbar: {
    show: true,
    links: [
      {
        label: "",
        href: "",
        show: true,
      },
    ],
    ctaLabel: "",
    ctaHref: "",
  },

  hero: {
    badgeText: "",
    titleLine1: "",
    titleLine2: "",
    subtitle: "",
    meta: [],
    primaryCta: { label: "", href: "" },
    secondaryCta: { label: "", href: "" },
    showCountdown: false,
    countdownTarget: "",
    showRegistrationForm: false,
  },

  speakers: {
    show: false,
    sectionLabel: "",
    sectionTitle: "",
    sectionSubtitle: "",
    items: [],
  },

  agenda: {
    show: false,
    sectionLabel: "",
    sectionTitle: "",
    sectionSubtitle: "",
    items: [],
  },

  highlights: {
    show: false,
    sectionLabel: "",
    sectionTitle: "",
    sectionSubtitle: "",
    items: [],
  },

  cta: {
    show: false,
    label: "",
    title: "",
    description: "",
    buttonLabel: "",
    buttonHref: "",
  },

  registrationForm: {
    show: false,
    badge: "",
    title: "",
    subtitle: "",
    seatsLabel: "",
    successDate: "",
    fields: {
      firstName: { show: false, required: false, placeholder: "" },
      lastName: { show: false, required: false, placeholder: "" },
      email: { show: false, required: false, placeholder: "" },
      phone: { show: false, required: false, placeholder: "" },
      profession: { show: false, required: false },
    },
  },

  footer: {
    show: false,
    links: [],
    copyright: "",
  },

  pastWebinars: [],
};

const useStyles = makeStyles(() => ({
  accordion: {
    backgroundColor: T.surface,
    border: `1px solid ${T.border}`,
    boxShadow: T.shadow,
    margin: "0 !important",
    borderRadius: `${T.radiusLg} !important`,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      boxShadow: T.shadowHover,
      borderColor: T.borderHover,
    },
    "&::before": { display: "none" },
  },
  summary: {
    padding: "0 24px",
    minHeight: "64px !important",
    "& .MuiAccordionSummary-content": {
      margin: "18px 0 !important",
      alignItems: "center",
      gap: "12px",
    },
    "& .MuiIconButton-root": {
      color: T.muted,
    },
  },
  details: {
    padding: "0 24px 28px",
    flexDirection: "column",
  },
  expandIcon: {
    color: "#00838c",
  },
}));

const DebouncedInput = React.memo(
  ({
    label,
    name,
    value,
    onChange,
    onBlur,
    error,
    touched,
    placeholder = "",
    multiline = false,
    rows = 1,
    icon,
    type,
  }) => {
    const [local, setLocal] = useState(value || "");
    const timer = useRef(null);

    React.useEffect(() => {
      setLocal(value || "");
    }, [value]);

    const handleChange = useCallback(
      (e) => {
        const v = e.target.value;
        setLocal(v);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onChange(e), 80);
      },
      [onChange],
    );

    const handleBlur = useCallback(
      (e) => {
        clearTimeout(timer.current);
        onChange(e);
        if (onBlur) onBlur(e);
      },
      [onChange, onBlur],
    );

    const hasError = error && touched;

    return (
      <FieldWrapper>
        <FieldLabel hasError={hasError}>
          {icon && <FieldIcon>{icon}</FieldIcon>}
          {_.upperCase(label)}
        </FieldLabel>
        <InputShell hasError={hasError}>
          {type === "textarea" ? (
            <CustomTextArea
              variant="outlined"
              className="input-white"
              value={local}
              name={name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              multiline={multiline}
              rows={5}
            />
          ) : (
            <CustomInputField
              variant="outlined"
              className="input-white"
              value={local}
              name={name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              multiline={multiline}
              rows={rows}
            />
          )}
        </InputShell>
        {hasError && <FieldError>{error}</FieldError>}
      </FieldWrapper>
    );
  },
);
DebouncedInput.displayName = "DebouncedInput";

const Toggle = ({ label, name, checked, onChange }) => (
  <ToggleRow>
    <CustomCheckbox
      type="checkbox"
      name={name}
      checked={checked || false}
      onChange={onChange}
    />
    <CheckBoxLabel>{label}</CheckBoxLabel>
  </ToggleRow>
);

const SectionBadge = ({ emoji, label, count }) => (
  <SummaryContent>
    <SectionName>{label}</SectionName>
    {count !== undefined && <CountPill>{count}</CountPill>}
  </SummaryContent>
);

const WebinarConfigEditor = ({ id }) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [webinarData, setWebinarData] = useState(EMPTY_WEBINAR);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoader({ state: true, message: "Fetching webinar data..." });
        const res = await SystemService.fetchWebinarConfigById(id);
        if (res?.data) {
          setWebinarData(res.data);
        } else {
          setWebinarData(EMPTY_WEBINAR);
        }
      } catch (err) {
        enqueueSnackbar("Failed to fetch webinar data", { variant: "error" });
        setWebinarData(EMPTY_WEBINAR);
      } finally {
        setLoader({ state: false });
      }
    };
    fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const s3Upload = useCallback(
    async (file, field, setFieldValue, setFieldError) => {
      if (file.type.includes("image/")) {
        try {
          setLoader({ state: true, message: `Uploading: ${file.name}` });
          const payload = { key: file.name };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Image uploaded successfully", {
              variant: "success",
            });
          }
        } catch (error) {
          setFieldError(field, "Could not upload this file");
          enqueueSnackbar("Failed to upload image", { variant: "error" });
        } finally {
          setLoader({ state: false });
        }
      } else {
        enqueueSnackbar("Please upload a valid image file", {
          variant: "error",
        });
      }
    },
    [setLoader, enqueueSnackbar],
  );

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoader({ state: true, message: "Saving webinar configuration…" });
      let res = "";
      const payload = {
        slug: values.slug || "",
        status: values.status || "upcoming",
        title: values.title || "",
        navbar: values.navbar || {},
        hero: values.hero || {},
        speakers: values.speakers || { show: false, items: [] },
        agenda: values.agenda || { show: false, items: [] },
        highlights: values.highlights || { show: false, items: [] },
        cta: values.cta || { show: false },
        registrationForm: values.registrationForm || {
          show: false,
          fields: {},
        },
        footer: values.footer || { show: false, links: [], copyright: "" },
      };
      if (values.id) {
        res = await SystemService.updateWebinarConfig(values.id, payload);
      } else {
        res = await SystemService.createWebinarConfig(payload);
      }
      enqueueSnackbar(res.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const mergeDeep = (target, source) => {
    if (!source || typeof source !== "object") return _.cloneDeep(target);

    const output = _.cloneDeep(target);

    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (Array.isArray(sourceValue)) {
        output[key] = sourceValue.length > 0 ? sourceValue : targetValue || [];
      } else if (sourceValue && typeof sourceValue === "object") {
        output[key] = mergeDeep(targetValue || {}, sourceValue);
      } else {
        output[key] =
          sourceValue !== undefined && sourceValue !== null
            ? sourceValue
            : targetValue;
      }
    });

    return output;
  };

  const initialValues = useMemo(() => {
    return mergeDeep(EMPTY_WEBINAR, webinarData || {}); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarData]);

  const onBreadcrumbClick = useCallback((selected) => {
    if (_.snakeCase(selected) === "configurations") {
      navigate("/dashboard/configurations");
    }
  }, []);

  return (
    <>
      <ActionBar
        breadcrumbs={["Configurations", "Webinar Setup"]}
        onBreadcrumbClick={onBreadcrumbClick}
      />
      <PageShell>
        <PageHeader>
          <HeaderBadge>Draft</HeaderBadge>
        </PageHeader>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            submitForm,
            setFieldError,
            resetForm,
          }) => (
            <EditorLayout>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge label="Meta Information" />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <TwoCol>
                    <DebouncedInput
                      label="Title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Webinar title"
                    />
                    <DebouncedInput
                      label="Slug"
                      name="slug"
                      value={values.slug}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="webinar-slug-here"
                    />
                    <DebouncedInput
                      label="Status"
                      name="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="upcoming · live · past"
                    />
                  </TwoCol>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge
                    label="Navbar"
                    count={values.navbar?.links?.length ?? 0}
                  />
                </AccordionSummary>

                <AccordionDetails className={classes.details}>
                  <Divider />

                  {/* Toggle */}
                  <ToggleGroup>
                    <Toggle
                      label="Show Navbar"
                      name="navbar.show"
                      checked={values.navbar?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>

                  {/* CTA Inputs */}
                  <TwoCol>
                    <DebouncedInput
                      label="CTA Label"
                      name="navbar.ctaLabel"
                      value={values.navbar?.ctaLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="CTA Link"
                      name="navbar.ctaHref"
                      value={values.navbar?.ctaHref}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>

                  {/* Links Array */}
                  <FieldArray name="navbar.links">
                    {({ push, remove }) => (
                      <ArrayBlock>
                        {values.navbar?.links?.map((link, index) => (
                          <ItemCard key={index}>
                            <ItemCardHeader>
                              <ItemCardIndex>Link {index + 1}</ItemCardIndex>

                              <DeleteBtn
                                type="button"
                                onClick={() => remove(index)}
                                title="Remove link"
                              >
                                <DeleteIcon fontSize="small" />
                              </DeleteBtn>
                            </ItemCardHeader>

                            <TwoCol>
                              <DebouncedInput
                                label="Label"
                                name={`navbar.links[${index}].label`}
                                value={link.label}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />

                              <DebouncedInput
                                label="Href"
                                name={`navbar.links[${index}].href`}
                                value={link.href}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </TwoCol>

                            <Toggle
                              label="Show Link"
                              name={`navbar.links[${index}].show`}
                              checked={link.show}
                              onChange={handleChange}
                            />
                          </ItemCard>
                        ))}

                        {/* Add Button */}
                        <AddBtn
                          type="button"
                          onClick={() =>
                            push({
                              label: "",
                              href: "",
                              show: true,
                            })
                          }
                        >
                          <AddIcon fontSize="small" /> Add New Link
                        </AddBtn>
                      </ArrayBlock>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge label="Hero Section" />
                </AccordionSummary>

                <AccordionDetails className={classes.details}>
                  <Divider />
                  <TwoCol>
                    <DebouncedInput
                      label="Title Line 1"
                      name="hero.titleLine1"
                      value={values.hero?.titleLine1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="Title Line 2"
                      name="hero.titleLine2"
                      value={values.hero?.titleLine2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="Subtitle"
                      name="hero.subtitle"
                      value={values.hero?.subtitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={3}
                      type="textarea"
                    />

                    <DebouncedInput
                      label="Badge Text"
                      name="hero.badgeText"
                      value={values.hero?.badgeText}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="Countdown Target"
                      name="hero.countdownTarget"
                      value={values.hero?.countdownTarget}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="2026-03-07T11:00:00+05:30"
                    />
                  </TwoCol>

                  {/* CTA Section */}
                  <Divider style={{ margin: "16px 0" }} />

                  <TwoCol>
                    {/* Primary CTA */}
                    <DebouncedInput
                      label="Primary CTA Label"
                      name="hero.primaryCta.label"
                      value={values.hero?.primaryCta?.label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="Primary CTA Link"
                      name="hero.primaryCta.href"
                      value={values.hero?.primaryCta?.href}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    {/* Secondary CTA */}
                    <DebouncedInput
                      label="Secondary CTA Label"
                      name="hero.secondaryCta.label"
                      value={values.hero?.secondaryCta?.label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />

                    <DebouncedInput
                      label="Secondary CTA Link"
                      name="hero.secondaryCta.href"
                      value={values.hero?.secondaryCta?.href}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>

                  {/* Meta Section (FIXED 3 ROWS) */}
                  <Divider style={{ margin: "16px 0" }} />

                  {(() => {
                    const metaData =
                      values.hero?.meta?.length === 3
                        ? values.hero.meta
                        : [
                            { icon: "calendar", label: "", sublabel: "" },
                            { icon: "clock", label: "", sublabel: "" },
                            { icon: "monitor", label: "", sublabel: "" },
                          ];

                    return metaData.map((item, index) => (
                      <TwoCol key={index}>
                        <DebouncedInput
                          label="Icon"
                          name={`hero.meta[${index}].icon`}
                          value={values.hero?.meta?.[index]?.icon || item.icon}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />

                        <DebouncedInput
                          label="Label"
                          name={`hero.meta[${index}].label`}
                          value={values.hero?.meta?.[index]?.label || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />

                        <DebouncedInput
                          label="Sub Label"
                          name={`hero.meta[${index}].sublabel`}
                          value={values.hero?.meta?.[index]?.sublabel || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </TwoCol>
                    ));
                  })()}

                  {/* Toggles */}
                  <ToggleGroup>
                    <Toggle
                      label="Show Countdown"
                      name="hero.showCountdown"
                      checked={values.hero?.showCountdown}
                      onChange={handleChange}
                    />

                    <Toggle
                      label="Show Registration Form"
                      name="hero.showRegistrationForm"
                      checked={values.hero?.showRegistrationForm}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge
                    label="Speakers"
                    count={values.speakers?.items?.length ?? 0}
                  />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show Speakers Section"
                      name="speakers.show"
                      checked={values.speakers?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Section Label"
                      name="speakers.sectionLabel"
                      value={values.speakers?.sectionLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Title"
                      name="speakers.sectionTitle"
                      value={values.speakers?.sectionTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Subtitle"
                      name="speakers.sectionSubtitle"
                      value={values.speakers?.sectionSubtitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>
                  <FieldArray name="speakers.items">
                    {({ push, remove }) => (
                      <ArrayBlock>
                        {values.speakers?.items?.map((speaker, index) => (
                          <ItemCard key={speaker.id || index}>
                            <ItemCardHeader>
                              <ItemCardIndex>Speaker {index + 1}</ItemCardIndex>
                              <DeleteBtn
                                type="button"
                                onClick={() => remove(index)}
                                title="Remove speaker"
                              >
                                <DeleteIcon fontSize="small" />
                              </DeleteBtn>
                            </ItemCardHeader>
                            <TwoCol>
                              <DebouncedInput
                                label="Name"
                                name={`speakers.items[${index}].name`}
                                value={speaker.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Designation"
                                name={`speakers.items[${index}].designation`}
                                value={speaker.designation}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Organization"
                                name={`speakers.items[${index}].org`}
                                value={speaker.org}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Tag"
                                name={`speakers.items[${index}].tag`}
                                value={speaker.tag}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Description"
                                name={`speakers.items[${index}].description`}
                                value={speaker.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                multiline
                                rows={2}
                                type="textarea"
                              />
                              <div>
                                <FieldLabel>Speaker Photo</FieldLabel>
                                <CustomFileUpload
                                  type="file"
                                  value={speaker.src}
                                  file={{
                                    name: getFileName(
                                      values?.speakers?.items[index].src,
                                    ),
                                  }}
                                  setFile={(file) =>
                                    s3Upload(
                                      file,
                                      `speakers.items[${index}].src`,
                                      setFieldValue,
                                      setFieldError,
                                    )
                                  }
                                  accept="image/*"
                                />
                              </div>
                            </TwoCol>
                            <Toggle
                              label="Show Speaker"
                              name={`speakers.items[${index}].show`}
                              checked={speaker.show}
                              onChange={handleChange}
                            />
                          </ItemCard>
                        ))}
                        <AddBtn
                          type="button"
                          onClick={() =>
                            push({
                              id: `s${Date.now()}`,
                              src: "",
                              name: "",
                              designation: "",
                              org: "",
                              tag: "Expert Speaker",
                              show: true,
                            })
                          }
                        >
                          <AddIcon fontSize="small" /> Add New Speaker
                        </AddBtn>
                      </ArrayBlock>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge
                    label="Agenda"
                    count={values.agenda?.items?.length ?? 0}
                  />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show Agenda Section"
                      name="agenda.show"
                      checked={values.agenda?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Section Label"
                      name="agenda.sectionLabel"
                      value={values.agenda?.sectionLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Title"
                      name="agenda.sectionTitle"
                      value={values.agenda?.sectionTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Subtitle"
                      name="agenda.sectionSubtitle"
                      value={values.agenda?.sectionSubtitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={2}
                      type="textarea"
                    />
                  </TwoCol>
                  <FieldArray name="agenda.items">
                    {({ push, remove }) => (
                      <ArrayBlock>
                        {values.agenda?.items?.map((item, index) => (
                          <ItemCard key={item.id || index}>
                            <ItemCardHeader>
                              <ItemCardIndex>
                                Agenda Item {index + 1}
                              </ItemCardIndex>
                              <DeleteBtn
                                type="button"
                                onClick={() => remove(index)}
                                title="Remove item"
                              >
                                <DeleteIcon fontSize="small" />
                              </DeleteBtn>
                            </ItemCardHeader>
                            <TwoCol>
                              <DebouncedInput
                                label="Heading"
                                name={`agenda.items[${index}].time`}
                                value={item.time}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="10:00 AM"
                              />
                              <DebouncedInput
                                label="Icon"
                                name={`agenda.items[${index}].icon`}
                                value={item.icon}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="👋"
                              />
                              <DebouncedInput
                                label="Title"
                                name={`agenda.items[${index}].title`}
                                value={item.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Description"
                                name={`agenda.items[${index}].desc`}
                                value={item.desc}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                multiline
                                rows={2}
                                type="textarea"
                              />
                            </TwoCol>
                            <Toggle
                              label="Show Item"
                              name={`agenda.items[${index}].show`}
                              checked={item.show}
                              onChange={handleChange}
                            />
                          </ItemCard>
                        ))}
                        <AddBtn
                          type="button"
                          onClick={() =>
                            push({
                              id: `a${Date.now()}`,
                              time: "",
                              icon: "👋",
                              title: "",
                              desc: "",
                              show: true,
                            })
                          }
                        >
                          <AddIcon fontSize="small" /> Add Agenda Item
                        </AddBtn>
                      </ArrayBlock>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge
                    label="Highlights"
                    count={values.highlights?.items?.length ?? 0}
                  />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show Highlights Section"
                      name="highlights.show"
                      checked={values.highlights?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Section Label"
                      name="highlights.sectionLabel"
                      value={values.highlights?.sectionLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Title"
                      name="highlights.sectionTitle"
                      value={values.highlights?.sectionTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Section Subtitle"
                      name="highlights.sectionSubtitle"
                      value={values.highlights?.sectionSubtitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={2}
                      type="textarea"
                    />
                  </TwoCol>
                  <FieldArray name="highlights.items">
                    {({ push, remove }) => (
                      <ArrayBlock>
                        {values.highlights?.items?.map((item, index) => (
                          <ItemCard key={item.id || index}>
                            <ItemCardHeader>
                              <ItemCardIndex>
                                Highlight {index + 1}
                              </ItemCardIndex>
                              <DeleteBtn
                                type="button"
                                onClick={() => remove(index)}
                                title="Remove highlight"
                              >
                                <DeleteIcon fontSize="small" />
                              </DeleteBtn>
                            </ItemCardHeader>
                            <TwoCol>
                              <DebouncedInput
                                label="Title"
                                name={`highlights.items[${index}].title`}
                                value={item.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Icon"
                                name={`highlights.items[${index}].icon`}
                                value={item.icon}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <DebouncedInput
                                label="Text"
                                name={`highlights.items[${index}].text`}
                                value={item.text}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                multiline
                                rows={3}
                                type="textarea"
                              />
                            </TwoCol>
                            <Toggle
                              label="Show Highlight"
                              name={`highlights.items[${index}].show`}
                              checked={item.show}
                              onChange={handleChange}
                            />
                          </ItemCard>
                        ))}
                        <AddBtn
                          type="button"
                          onClick={() =>
                            push({
                              id: `h${Date.now()}`,
                              icon: "book",
                              title: "",
                              text: "",
                              show: true,
                            })
                          }
                        >
                          <AddIcon fontSize="small" /> Add Highlight
                        </AddBtn>
                      </ArrayBlock>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge label="CTA Section" />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show CTA Section"
                      name="cta.show"
                      checked={values.cta?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Label"
                      name="cta.label"
                      value={values.cta?.label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Title"
                      name="cta.title"
                      value={values.cta?.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Description"
                      name="cta.description"
                      value={values.cta?.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={3}
                      type="textarea"
                    />
                    <DebouncedInput
                      label="Button Label"
                      name="cta.buttonLabel"
                      value={values.cta?.buttonLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Button Href"
                      name="cta.buttonHref"
                      value={values.cta?.buttonHref}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>
                </AccordionDetails>
              </Accordion>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge label="Registration Form" />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show Registration Form"
                      name="registrationForm.show"
                      checked={values.registrationForm?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Badge"
                      name="registrationForm.badge"
                      value={values.registrationForm?.badge}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Title"
                      name="registrationForm.title"
                      value={values.registrationForm?.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Subtitle"
                      name="registrationForm.subtitle"
                      value={values.registrationForm?.subtitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Seats Label"
                      name="registrationForm.seatsLabel"
                      value={values.registrationForm?.seatsLabel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <DebouncedInput
                      label="Success Date"
                      name="registrationForm.successDate"
                      value={values.registrationForm?.successDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>
                  <FieldGroupLabel>Form Fields Visibility</FieldGroupLabel>
                  <ToggleGroup>
                    {[
                      "firstName",
                      "lastName",
                      "email",
                      "phone",
                      "profession",
                    ].map((key) => (
                      <Toggle
                        key={key}
                        label={`Show ${_.startCase(key)}`}
                        name={`registrationForm.fields.${key}.show`}
                        checked={values.registrationForm?.fields?.[key]?.show}
                        onChange={handleChange}
                      />
                    ))}
                  </ToggleGroup>
                </AccordionDetails>
              </Accordion>

              {/* ── FOOTER ── */}
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  className={classes.summary}
                  expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                >
                  <SectionBadge
                    label="Footer"
                    count={values.footer?.links?.length ?? 0}
                  />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Divider />
                  <ToggleGroup>
                    <Toggle
                      label="Show Footer"
                      name="footer.show"
                      checked={values.footer?.show}
                      onChange={handleChange}
                    />
                  </ToggleGroup>
                  <TwoCol>
                    <DebouncedInput
                      label="Copyright Text"
                      name="footer.copyright"
                      value={values.footer?.copyright}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </TwoCol>

                  <FieldGroupLabel>Footer Links</FieldGroupLabel>
                  <FieldArray name="footer.links">
                    {({ push, remove }) => (
                      <ArrayBlock>
                        {values.footer?.links?.map((link, index) => (
                          <LinkRow key={index}>
                            <LinkRowInput>
                              <DebouncedInput
                                label={`Link ${index + 1}`}
                                name={`footer.links[${index}]`}
                                value={link}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="https://example.com"
                              />
                            </LinkRowInput>
                            <DeleteBtn
                              type="button"
                              onClick={() => remove(index)}
                              title="Remove link"
                              style={{ marginTop: 20 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </DeleteBtn>
                          </LinkRow>
                        ))}
                        <AddBtn type="button" onClick={() => push("")}>
                          <AddIcon fontSize="small" /> Add Footer Link
                        </AddBtn>
                      </ArrayBlock>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
              <SaveBar>
                <SaveHint>
                  All changes are saved to the database on submit.
                </SaveHint>
                <SaveButton
                  type="button"
                  onClick={submitForm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner /> Saving…
                    </>
                  ) : (
                    <> Save All Changes</>
                  )}
                </SaveButton>
              </SaveBar>
            </EditorLayout>
          )}
        </Formik>
      </PageShell>
    </>
  );
};

export default WebinarConfigEditor;

const T = {
  teal: "#00838c",
  tealLight: "rgba(0,131,140,0.08)",
  tealMid: "rgba(0,131,140,0.18)",
  tealGlow: "rgba(0,131,140,0.25)",
  dark: "#0d1b2a",
  mid: "#3d5166",
  muted: "#7a8fa6",
  border: "#e3e8ef",
  borderHover: "#c5d0dc",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  surfaceDeep: "#f1f5f9",
  danger: "#e53e3e",
  dangerLight: "rgba(229,62,62,0.08)",
  radius: "12px",
  radiusLg: "16px",
  shadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
  shadowHover: "0 4px 20px rgba(0,131,140,0.14), 0 1px 4px rgba(0,0,0,0.06)",
  fontDisplay: "'Plus Jakarta Sans', 'DM Sans', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const PageShell = styled.div`
  padding: 40px 48px 80px;
  background: ${T.surfaceAlt};
  min-height: calc(100vh - 64px);
  font-family: ${T.fontBody};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 36px;
  animation: ${fadeUp} 0.4s ease both;
`;

const HeaderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: rgba(0, 131, 140, 0.08);
  color: #00838c;
  border: 1px solid rgba(0, 131, 140, 0.18);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-top: 6px;
`;

const EditorLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > * {
    animation: ${fadeUp} 0.35s ease both;
  }
  & > *:nth-child(1) {
    animation-delay: 0.04s;
  }
  & > *:nth-child(2) {
    animation-delay: 0.08s;
  }
  & > *:nth-child(3) {
    animation-delay: 0.12s;
  }
  & > *:nth-child(4) {
    animation-delay: 0.16s;
  }
  & > *:nth-child(5) {
    animation-delay: 0.2s;
  }
  & > *:nth-child(6) {
    animation-delay: 0.24s;
  }
  & > *:nth-child(7) {
    animation-delay: 0.28s;
  }
  & > *:nth-child(8) {
    animation-delay: 0.32s;
  }
  & > *:nth-child(9) {
    animation-delay: 0.36s;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${T.border};
  margin-bottom: 24px;
`;

const SummaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionName = styled.h3`
  font-family: ${T.fontDisplay};
  font-size: 15px;
  font-weight: 600;
  color: #293461;
  margin: 0;
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(0, 131, 140, 0.08);
  border: 1px solid rgba(0, 131, 140, 0.18);
  color: #00838c;
  font-size: 11px;
  font-weight: 700;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: ${slideIn} 0.25s ease both;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${({ hasError }) => (hasError ? T.danger : T.muted)};
  margin-bottom: 2px;
`;

const FieldIcon = styled.span`
  font-size: 12px;
`;

const InputShell = styled.div`
  position: relative;
  border-radius: 8px;
  transition: box-shadow 0.15s ease;

  &:focus-within {
    box-shadow: 0 0 0 3px
      ${({ hasError }) => (hasError ? "rgba(229,62,62,0.15)" : T.tealGlow)};
  }
`;

const FieldError = styled.span`
  font-size: 11px;
  color: ${T.danger};
`;

const ToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-bottom: 20px;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 131, 140, 0.08);
  }
`;

const FieldGroupLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${T.muted};
  margin: 4px 0 12px;
`;

const ArrayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemCard = styled.div`
  background: ${T.surfaceDeep};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  padding: 20px 20px 12px;
  transition:
    box-shadow 0.2s,
    border-color 0.2s;
  animation: ${fadeUp} 0.25s ease both;

  &:hover {
    border-color: ${T.borderHover};
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
`;

const ItemCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${T.border};
`;

const ItemCardIndex = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.BTN_GREEN};
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #00838c;
  background: transparent;
  color: #00838c;
  font-size: 13px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  transition:
    background 0.15s,
    box-shadow 0.15s;
  align-self: flex-start;
  margin-top: 4px;

  &:hover {
    background: rgba(0, 131, 140, 0.08);
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.25);
  }

  svg {
    font-size: 16px !important;
  }
`;

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: ${T.muted};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${T.dangerLight};
    color: ${T.danger};
    border-color: rgba(229, 62, 62, 0.25);
  }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

const LinkRowInput = styled.div`
  flex: 1;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  margin-right: 6px;
`;

const SaveBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radiusLg};
  box-shadow: ${T.shadow};
  margin-top: 8px;
  animation: ${fadeUp} 0.4s 0.4s ease both;
`;

const SaveHint = styled.p`
  font-size: 13px;
  color: ${T.muted};
  margin: 0;
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 32px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #00838c 0%, #006e78 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: ${T.fontDisplay};
  cursor: pointer;
  letter-spacing: 0.2px;
  transition:
    opacity 0.15s,
    box-shadow 0.15s,
    transform 0.1s;
  box-shadow: 0 4px 14px rgba(0, 131, 140, 0.35);

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(0, 131, 140, 0.45);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckBoxLabel = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
  cursor: inherit;
`;
